import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Wizard, useWizardSteps, WizardStep } from '../../components/Wizard';
import { adminApi, RetailerIndustry } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';

const WIZARD_STEPS: WizardStep[] = [
  { id: 'retailer', title: 'Create Retailer' },
  { id: 'path', title: 'Template Path' },
  { id: 'template', title: 'Add Template' },
  { id: 'summary', title: 'Summary' },
];

interface Props {
  onClose: () => void;
}

export function RetailerSetupWizard({ onClose }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { step, next, back, isLast } = useWizardSteps(WIZARD_STEPS.length);

  // Step 1: Retailer data
  const [retailerData, setRetailerData] = useState({
    retailerId: '',
    name: '',
    industry: '' as RetailerIndustry | '',
    ediPartnerId: '',
    website: '',
    contactEmail: '',
    notes: '',
  });

  // Step 2: Template path choice
  const [templatePath, setTemplatePath] = useState<'manual' | 'onboarding' | 'skip'>('manual');

  // Step 3: Template data (if manual)
  const [templateData, setTemplateData] = useState({
    labelType: 'shipping',
    templateName: '',
    description: '',
    zplContent: '',
  });

  // Tracking created IDs
  const [createdRetailerId, setCreatedRetailerId] = useState('');
  const [createdTemplateKey, setCreatedTemplateKey] = useState('');

  const { data: industriesData } = useQuery({
    queryKey: ['admin', 'industries'],
    queryFn: () => adminApi.getIndustries(),
  });
  const industries = industriesData?.industries ?? industriesData ?? [];

  const createRetailerMut = useMutation({
    mutationFn: () =>
      adminApi.createRetailer({
        retailerId: retailerData.retailerId,
        name: retailerData.name,
        industry: retailerData.industry as RetailerIndustry,
        ediPartnerId: retailerData.ediPartnerId || undefined,
        website: retailerData.website || undefined,
        contactEmail: retailerData.contactEmail || undefined,
        notes: retailerData.notes || undefined,
      }),
    onSuccess: (data) => {
      setCreatedRetailerId(data.retailerId ?? retailerData.retailerId);
      queryClient.invalidateQueries({ queryKey: ['admin', 'retailers'] });
      addToast({ type: 'success', message: `Retailer "${retailerData.name}" created` });
      next();
    },
    onError: (err) => {
      addToast({ type: 'error', message: `Failed: ${err instanceof Error ? err.message : String(err)}` });
    },
  });

  const createTemplateMut = useMutation({
    mutationFn: () =>
      adminApi.createTemplate({
        industry: retailerData.industry as string,
        company: createdRetailerId || retailerData.retailerId,
        labelType: templateData.labelType,
        templateName: templateData.templateName || undefined,
        description: templateData.description || undefined,
        zplContent: templateData.zplContent,
      }),
    onSuccess: (data) => {
      setCreatedTemplateKey(data.templateKey ?? '');
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'retailers'] });
      addToast({ type: 'success', message: 'Template created' });
      next();
    },
    onError: (err) => {
      addToast({ type: 'error', message: `Failed: ${err instanceof Error ? err.message : String(err)}` });
    },
  });

  // Validation per step
  const canAdvance = (() => {
    switch (step) {
      case 0:
        return !!(retailerData.retailerId && retailerData.name && retailerData.industry);
      case 1:
        return !!templatePath;
      case 2:
        if (templatePath === 'skip' || templatePath === 'onboarding') return true;
        return !!templateData.zplContent.trim();
      case 3:
        return true;
      default:
        return false;
    }
  })();

  const handleNext = () => {
    if (step === 0) {
      createRetailerMut.mutate();
      return;
    }
    if (step === 1) {
      if (templatePath === 'skip') {
        // Jump to summary (step 3) by calling next twice
        next();
        setTimeout(() => next(), 0);
        return;
      }
      if (templatePath === 'onboarding') {
        next();
        setTimeout(() => next(), 0);
        return;
      }
      next();
      return;
    }
    if (step === 2) {
      if (templatePath === 'manual') {
        createTemplateMut.mutate();
        return;
      }
      next();
      return;
    }
    // Step 3 (summary) — close wizard
    onClose();
  };

  const isSubmitting = createRetailerMut.isPending || createTemplateMut.isPending;

  return (
    <Wizard
      title="New Retailer Setup"
      steps={WIZARD_STEPS}
      activeStep={step}
      onClose={onClose}
      onBack={back}
      onNext={handleNext}
      canAdvance={canAdvance}
      isLastStep={isLast}
      isSubmitting={isSubmitting}
    >
      {step === 0 && (
        <Step1_CreateRetailer
          data={retailerData}
          onChange={setRetailerData}
          industries={Array.isArray(industries) ? industries : []}
          error={createRetailerMut.isError ? (createRetailerMut.error as Error).message : undefined}
        />
      )}
      {step === 1 && (
        <Step2_TemplatePath path={templatePath} onChange={setTemplatePath} />
      )}
      {step === 2 && (
        <Step3_AddTemplate
          path={templatePath}
          data={templateData}
          onChange={setTemplateData}
          retailerId={createdRetailerId}
        />
      )}
      {step === 3 && (
        <Step4_Summary
          retailerId={createdRetailerId}
          retailerName={retailerData.name}
          templatePath={templatePath}
          templateKey={createdTemplateKey}
          onGoToRetailer={() => { onClose(); navigate(`/admin/retailers`); }}
          onGoToTemplates={() => { onClose(); navigate(`/admin/templates?company=${createdRetailerId}`); }}
          onGoToOnboarding={() => { onClose(); navigate('/admin/onboarding'); }}
        />
      )}
    </Wizard>
  );
}

// ─── Step Components ─────────────────────────────────────

type RetailerFormData = {
  retailerId: string;
  name: string;
  industry: RetailerIndustry | '';
  ediPartnerId: string;
  website: string;
  contactEmail: string;
  notes: string;
};

function Step1_CreateRetailer({
  data,
  onChange,
  industries,
  error,
}: {
  data: RetailerFormData;
  onChange: (d: RetailerFormData) => void;
  industries: Array<{ id: string; name: string; description: string }>;
  error?: string;
}) {
  const set = <K extends keyof RetailerFormData>(field: K, value: RetailerFormData[K]) =>
    onChange({ ...data, [field]: value });

  return (
    <div className="space-y-4">
      <p className="text-gray-600 text-sm mb-2">
        Enter the basic information for the new retailer. The Retailer ID becomes the permanent key — choose carefully.
      </p>

      <div>
        <label className="label">Retailer ID *</label>
        <input
          className="input"
          value={data.retailerId}
          onChange={(e) => set('retailerId', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
          placeholder="walmart, buc-ees, home-depot"
        />
        <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers, and hyphens only</p>
      </div>

      <div>
        <label className="label">Display Name *</label>
        <input
          className="input"
          value={data.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="Walmart, Buc-ee's"
        />
      </div>

      <div>
        <label className="label">Industry *</label>
        <select
          className="input"
          value={data.industry}
          onChange={(e) => set('industry', e.target.value as RetailerIndustry | '')}
        >
          <option value="">Select industry...</option>
          {industries.map((ind) => (
            <option key={ind.id} value={ind.id}>{ind.name} — {ind.description}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">EDI Partner ID</label>
          <input className="input" value={data.ediPartnerId} onChange={(e) => set('ediPartnerId', e.target.value)} placeholder="Optional" />
        </div>
        <div>
          <label className="label">Contact Email</label>
          <input className="input" type="email" value={data.contactEmail} onChange={(e) => set('contactEmail', e.target.value)} placeholder="Optional" />
        </div>
      </div>

      {error && <p className="text-red-600 text-sm bg-red-50 p-2 rounded">{error}</p>}
    </div>
  );
}

function Step2_TemplatePath({
  path,
  onChange,
}: {
  path: 'manual' | 'onboarding' | 'skip';
  onChange: (p: 'manual' | 'onboarding' | 'skip') => void;
}) {
  const options = [
    {
      id: 'manual' as const,
      icon: '✏️',
      title: 'Create Template Manually',
      description: 'Paste ZPL content now and create the first template for this retailer.',
    },
    {
      id: 'onboarding' as const,
      icon: '🤖',
      title: 'Use AI Onboarding Pipeline',
      description: 'Submit specification URLs and let the AI pipeline extract and compose templates automatically.',
    },
    {
      id: 'skip' as const,
      icon: '⏭️',
      title: 'Skip for Now',
      description: 'Create the retailer entity only. Add templates later from the Templates page.',
    },
  ];

  return (
    <div className="space-y-4">
      <p className="text-gray-600 text-sm mb-2">
        How would you like to set up templates for this retailer?
      </p>
      {options.map((opt) => (
        <button
          key={opt.id}
          onClick={() => onChange(opt.id)}
          className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
            path === opt.id
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-start gap-3">
            <span className="text-2xl">{opt.icon}</span>
            <div>
              <p className="font-medium text-gray-900">{opt.title}</p>
              <p className="text-sm text-gray-600 mt-1">{opt.description}</p>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function Step3_AddTemplate({
  path,
  data,
  onChange,
  retailerId,
}: {
  path: 'manual' | 'onboarding' | 'skip';
  data: { labelType: string; templateName: string; description: string; zplContent: string };
  onChange: (d: typeof data) => void;
  retailerId: string;
}) {
  const set = (field: string, value: string) => onChange({ ...data, [field]: value });

  if (path === 'onboarding') {
    return (
      <div className="text-center py-8">
        <p className="text-4xl mb-4">🤖</p>
        <p className="text-gray-700 font-medium">AI pipeline selected</p>
        <p className="text-gray-500 text-sm mt-2">
          After finishing this wizard, navigate to the Onboarding page to submit specification URLs 
          for <strong>{retailerId}</strong>.
        </p>
      </div>
    );
  }

  if (path === 'skip') {
    return (
      <div className="text-center py-8">
        <p className="text-4xl mb-4">⏭️</p>
        <p className="text-gray-700 font-medium">Skipping template creation</p>
        <p className="text-gray-500 text-sm mt-2">
          You can add templates later from the Templates page.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-gray-600 text-sm mb-2">
        Create the first template for <strong>{retailerId}</strong>. You can add more templates later.
      </p>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Label Type *</label>
          <select className="input" value={data.labelType} onChange={(e) => set('labelType', e.target.value)}>
            <option value="shipping">Shipping</option>
            <option value="carton">Carton</option>
            <option value="pallet">Pallet</option>
            <option value="mixed">Mixed</option>
          </select>
        </div>
        <div>
          <label className="label">Template Name</label>
          <input
            className="input"
            value={data.templateName}
            onChange={(e) => set('templateName', e.target.value)}
            placeholder="shipping-4x6"
          />
          <p className="text-xs text-gray-500 mt-1">Optional — auto-generated if blank</p>
        </div>
      </div>

      <div>
        <label className="label">Description</label>
        <input
          className="input"
          value={data.description}
          onChange={(e) => set('description', e.target.value)}
          placeholder="Standard 4×6 shipping label for..."
        />
      </div>

      <div>
        <label className="label">ZPL Content *</label>
        <textarea
          className="input font-mono text-xs"
          rows={10}
          value={data.zplContent}
          onChange={(e) => set('zplContent', e.target.value)}
          placeholder={'^XA\n^FO50,50^A0N,40,40^FD{{shipTo.name}}^FS\n^XZ'}
        />
        <p className="text-xs text-gray-500 mt-1">Use Handlebars syntax for dynamic fields: {'{{fieldName}}'}</p>
      </div>
    </div>
  );
}

function Step4_Summary({
  retailerId,
  retailerName,
  templatePath,
  templateKey,
  onGoToRetailer,
  onGoToTemplates,
  onGoToOnboarding,
}: {
  retailerId: string;
  retailerName: string;
  templatePath: 'manual' | 'onboarding' | 'skip';
  templateKey: string;
  onGoToRetailer: () => void;
  onGoToTemplates: () => void;
  onGoToOnboarding: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <p className="text-4xl mb-2">✅</p>
        <h3 className="text-lg font-semibold text-gray-900">Setup Complete!</h3>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Retailer</span>
          <span className="font-medium">{retailerName} ({retailerId})</span>
        </div>
        {templatePath === 'manual' && templateKey && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Template</span>
            <span className="font-mono text-xs">{templateKey}</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Template Method</span>
          <span className="font-medium">
            {templatePath === 'manual' ? 'Created manually' : templatePath === 'onboarding' ? 'AI pipeline (pending)' : 'Skipped'}
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">Next steps:</p>
        <div className="grid gap-2">
          <button
            onClick={onGoToRetailer}
            className="text-left px-4 py-3 border rounded-lg hover:bg-gray-50 text-sm transition-colors"
          >
            📋 View retailer list
          </button>
          {templatePath === 'manual' && (
            <button
              onClick={onGoToTemplates}
              className="text-left px-4 py-3 border rounded-lg hover:bg-gray-50 text-sm transition-colors"
            >
              🏷️ View templates for {retailerId}
            </button>
          )}
          {templatePath === 'onboarding' && (
            <button
              onClick={onGoToOnboarding}
              className="text-left px-4 py-3 border rounded-lg hover:bg-gray-50 text-sm transition-colors"
            >
              🤖 Go to Onboarding Pipeline to submit specs
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
